import React from 'react';
import TemplateRenderer from 'PDF/TemplateEngine/TemplateRenderer';
import { resolveDocDesign } from 'PDF/TemplateEngine/resolveDocDesign';
import { mapInformeData, withTenantTexts } from 'PDF/documents/informeMantencion';
import MantencionDocument from './MantencionDocument';

// Mismo criterio que InformeMantencionDownload.jsx: SignaturesSection lee las
// firmas desde `data`, NO desde `config` (ver ese componente y
// SignaturesSection.jsx). Por eso las URLs resueltas y los nombres se
// inyectan acá, en el objeto que consume TemplateRenderer.
function withSignatures(data, signatures, mantencionData) {
  return {
    ...data,
    specialist_name: mantencionData?.Responsable?.fullname || null,
    specialist_signature: signatures?.firma_spec || null,
    recepcionist_name: mantencionData?.name_recep || null,
    recepcionist_charge: mantencionData?.cargo_recep || null,
    recepcionist_signature: signatures?.firma_recep || null,
  };
}

/**
 * Arma el elemento React del informe de mantención a enviar por correo,
 * eligiendo el mismo renderer (lowcode vs legacy) que usa la pantalla de
 * descarga (InformeMantencionDownload / MantencionDowload), para que el PDF
 * archivado/enviado sea idéntico al que el usuario descarga.
 *
 * Función PURA — sin fetch, sin hooks. El caller (useMantencionSend.jsx para
 * el flujo online, sendMantencionPdf.js para el daemon offline) es
 * responsable de resolver template/staticData/config/colors/signatures antes
 * de invocarla.
 *
 * @param {object} params
 * @param {{mantencion:object, Ascensores:object[], actividades:object[]}} params.mantencionPayload
 *   - `mantencion` debe traer `design_version`, `Cliente`, `Responsable` y
 *     (para legacy) `firma_spec`/`firma_recep` ya resueltos a URL.
 * @param {object|null} params.template - Plantilla lowcode (o null si el
 *   fetch falló / no hay plantilla persistida).
 * @param {object[]|null} params.staticData - Filas tenant_text (documentType
 *   'informe_mantencion' para lowcode, 'mantencion' para legacy — cada
 *   renderer filtra las suyas).
 * @param {object} params.config - Config/tenant para Header/Footer/logo.
 * @param {{primary:string, secondary:string}} params.colors - Colores del
 *   tenant para el TemplateRenderer (branch lowcode).
 * @param {{firma_spec:string|null, firma_recep:string|null}} params.signatures
 *   - URLs de firma ya resueltas (branch lowcode).
 * @returns {React.ReactElement}
 */
export function buildInformeMantencionDoc({
  mantencionPayload,
  template,
  staticData,
  config,
  colors,
  signatures,
}) {
  const { mantencion, Ascensores, actividades } = mantencionPayload || {};
  const design = resolveDocDesign(mantencion, template);

  if (design === 'lowcode') {
    const baseData = mapInformeData({
      mantencion,
      Cliente: mantencion?.Cliente,
      Responsable: mantencion?.Responsable,
      actividades,
    });
    const dataWithTexts = withTenantTexts(baseData, staticData);
    const data = withSignatures(dataWithTexts, signatures, mantencion);
    return <TemplateRenderer template={template} data={data} config={config} colors={colors} />;
  }

  return (
    <MantencionDocument
      Ascensores={Ascensores}
      mantencion={mantencion}
      config={config}
      actividades={actividades}
      staticData={staticData}
    />
  );
}
